import { ref, type Ref, onMounted } from "vue";
import * as Cesium from "cesium";

export function useViewManagement(viewer: Ref<Cesium.Viewer | null>) {
    const hasSavedView = ref(false);

    const checkSavedView = (): void => {
        const savedView = localStorage.getItem("cesium-saved-view");
        hasSavedView.value = savedView !== null;
    };

    const saveView = (): void => {
        if (!viewer.value) return;

        try {
            const camera = viewer.value.camera;
            const view = {
                position: camera.position.clone(),
                direction: camera.direction.clone(),
                up: camera.up.clone(),
                right: camera.right.clone(),
            };

            localStorage.setItem(
                "cesium-saved-view",
                JSON.stringify({
                    position: {
                        x: view.position.x,
                        y: view.position.y,
                        z: view.position.z,
                    },
                    direction: {
                        x: view.direction.x,
                        y: view.direction.y,
                        z: view.direction.z,
                    },
                    up: {
                        x: view.up.x,
                        y: view.up.y,
                        z: view.up.z,
                    },
                    right: {
                        x: view.right.x,
                        y: view.right.y,
                        z: view.right.z,
                    },
                })
            );

            hasSavedView.value = true;
            console.log("View saved successfully");
        } catch (error) {
            console.error("Error saving view:", error);
        }
    };

    const loadView = (): void => {
        if (!viewer.value) return;

        try {
            const savedViewData = localStorage.getItem("cesium-saved-view");
            if (!savedViewData) {
                return;
            }

            const view = JSON.parse(savedViewData);
            const camera = viewer.value.camera;

            // Use flyTo for smooth animated transition
            camera.flyTo({
                destination: Cesium.Cartesian3.fromArray([
                    view.position.x,
                    view.position.y,
                    view.position.z,
                ]),
                orientation: {
                    direction: Cesium.Cartesian3.fromArray([
                        view.direction.x,
                        view.direction.y,
                        view.direction.z,
                    ]),
                    up: Cesium.Cartesian3.fromArray([view.up.x, view.up.y, view.up.z]),
                },
                duration: 2.0, // 2 second animation
            });

            console.log("View loaded successfully");
        } catch (error) {
            console.error("Error loading view:", error);
        }
    };

    const resetView = (place: string): void => {
        if (!viewer.value) return;

        if (place === 'Varennes') {
            viewer.value.camera.setView({
                destination: Cesium.Cartesian3.fromDegrees(-73.427263, 45.678303, 1000.0),
                orientation: {
                    heading: Cesium.Math.toRadians(0.0),
                    pitch: Cesium.Math.toRadians(-40.0),
                    roll: 0.0,
                },
            });
        } else if (place === 'Office') {
            viewer.value.camera.setView({
                destination: Cesium.Cartesian3.fromDegrees(-73.541873, 45.555029, 200.0),
                orientation: {
                    heading: Cesium.Math.toRadians(0.0),
                    pitch: Cesium.Math.toRadians(-40.0),
                    roll: 0.0,
                },
            });
        } else if (place === 'Factory') {
            viewer.value.camera.setView({
                destination: Cesium.Cartesian3.fromDegrees(-73.547567, 45.488085, 200.0),
                orientation: {
                    heading: Cesium.Math.toRadians(0.0),
                    pitch: Cesium.Math.toRadians(-40.0),
                    roll: 0.0,
                },
            });
        }

    };

    // Check for saved view on mount
    onMounted(() => {
        checkSavedView();
    });

    return {
        hasSavedView,
        saveView,
        loadView,
        resetView,
        checkSavedView,
    };
}